import { CSSProperties, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { AgenticViewer } from '@amzn/work-spaces-agentic-web-viewer';
import '@amzn/work-spaces-agentic-web-viewer/styles.css';
import { CoreFactory } from '@/bridge/factory/CoreFactory';
import { useReportHeartbeatTask } from '@/hooks/useReportHeartbeatTask';
import { useSessionStore } from '@/stores/session';
import { isAgenticWorkspace } from '@views/Stream/agentic';

// The viewer tags its own lines and writes them through this logger, so they
// reach the same log upload as the rest of the client. One shared instance.
const logger = CoreFactory.getLogger();

// Fixed and viewport-sized: the viewer's docking shell fills its parent, so it
// needs a definite box, and pinning it to the viewport keeps route content
// reflowing underneath from disturbing it while it holds live sockets.
const containerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  zIndex: 1000,
};

/*
 Route element for an active agentic session at /agentic. Maxibon writes the
 'wsp_context' handoff before it routes here, and the viewer reads that same
 handoff when it mounts. A cold deep-link with no handoff has nothing to connect
 to, and a standard WorkSpace has no agent to talk to, so both fall back to the
 app entry rather than opening a viewer that cannot work.
*/
const AgenticRoute: React.FC = () => {
  // Scope the viewer's saved chats to this WorkSpace. resourceId is the
  // GetResources ResourceId, persisted in the session store's sessionStorage,
  // so it is still present when /agentic is reloaded from the wsp_context
  // handoff. Empty means no WorkSpace, so hand the viewer undefined, not ''.
  const resourceId = useSessionStore((state) => state.resourceId);
  // Same check and persisted GetResources result as Maxibon, so a reload needs
  // no broker call. Routing only, the WorkSpace host enforces access.
  const resourceDetails = useSessionStore(
    (state) => state.resource?.ResourceDetails
  );
  const isAgentic = isAgenticWorkspace(resourceDetails);
  const routeShowsViewer =
    window.sessionStorage.getItem('wsp_context') !== null && isAgentic;
  const { startReportingHeartbeat } = useReportHeartbeatTask();

  // wsp.html reports the heartbeat on the standard path. Without it, the broker
  // ends a SAML or IAM Identity Center session about 25 minutes after sign-in.
  useEffect(() => {
    if (routeShowsViewer) {
      startReportingHeartbeat();
    }
  }, [routeShowsViewer]);

  if (!routeShowsViewer) {
    return <Navigate to="/" replace />;
  }

  return (
    <div style={containerStyle}>
      <AgenticViewer workspaceId={resourceId || undefined} logger={logger} />
    </div>
  );
};

export default AgenticRoute;
